/**
 * Maps page
 * Full-screen map visualization of scraped places
 *
 * HYBRID CLUSTERING SYSTEM for optimal performance with 10k+ places:
 * - World/continent view (zoom < 10): Server-side clusters as circles
 * - City+ view (zoom >= 10): Client-side clustering with MarkerClusterGroup
 * - Toggle and radius slider control client-side clustering behavior
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, Spin } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import type { Map as LeafletMap } from 'leaflet';
import { MapContainer } from '@/components/maps/MapContainer';
import { MapFilterBar, PlaceMapMarkers } from '@/components/maps-page';
import { PlaceModal } from '@/components/results/PlaceModal';
import { useMapPlaces } from '@/hooks/useMapPlaces';
import { usePlace } from '@/hooks/usePlaces';
import { useVisibleCategories, useNameBlacklistActive } from '@/hooks/useSettings';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { useMapFiltersStore } from '@/stores/useMapFiltersStore';
import type { MapPlace, MapCluster } from '@/types/place';
import type { Place } from '@/types/place';
import type { Bounds } from '@/types/grid';

const { Title } = Typography;

/**
 * Convert bounds to Leaflet format
 */
function boundsToLeaflet(bounds: Bounds): [[number, number], [number, number]] {
  return [
    [bounds.south, bounds.west],
    [bounds.north, bounds.east],
  ];
}

export function MapsPage() {
  const {
    selectedPlaceId,
    setSelectedPlace,
    clusteringEnabled,
    clusterRadius,
    category,
    country,
    searchTerm,
    searchQuery,
  } = useMapFiltersStore();

  const { isAdmin } = useSiteAuth();

  // Curated categories filter
  const { data: visibleCategoriesList = [] } = useVisibleCategories();
  const hasCuratedCategories = visibleCategoriesList.length > 0;

  const [curatedCategoriesEnabled, setCuratedCategoriesEnabled] = useState(() => {
    const stored = localStorage.getItem('maps-curated-categories-enabled');
    return stored === null ? true : stored === 'true';
  });

  const handleCuratedCategoriesToggle = (enabled: boolean) => {
    setCuratedCategoriesEnabled(enabled);
    localStorage.setItem('maps-curated-categories-enabled', enabled.toString());
  };

  // Server-side curated categories filter
  const useCuratedFilter = hasCuratedCategories && curatedCategoriesEnabled;

  // Name blacklist filter
  const { data: hasBlacklist = false } = useNameBlacklistActive();
  const [nameBlacklistEnabled, setNameBlacklistEnabled] = useState(() => {
    const stored = localStorage.getItem('maps-name-blacklist-enabled');
    return stored === null ? true : stored === 'true';
  });

  const handleNameBlacklistToggle = (enabled: boolean) => {
    setNameBlacklistEnabled(enabled);
    localStorage.setItem('maps-name-blacklist-enabled', enabled.toString());
  };

  const useBlacklist = hasBlacklist && (isAdmin ? nameBlacklistEnabled : true);

  // Map instance, viewport, and zoom
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [visibleBounds, setVisibleBounds] = useState<Bounds | null>(null);
  const [zoom, setZoom] = useState(10);

  // Fetch map data - THREE-TIER zoom system
  const {
    clusters,
    places,
    totalInBounds,
    clustered,
    tier,
    initialBounds,
    isLoading,
    isFetching,
  } = useMapPlaces({
    viewportBounds: visibleBounds,
    zoom,
    debounceMs: 200,
    category: category && category.length > 0 ? category : undefined,
    curatedCategoriesOnly: useCuratedFilter || undefined,
    nameBlacklistActive: useBlacklist || undefined,
    searchTerm: searchTerm && searchTerm.length > 0 ? searchTerm : undefined,
    search: searchQuery || undefined,
    country: country && country.length > 0 ? country : undefined,
  });

  // Selected place for modal
  const [modalPlaceId, setModalPlaceId] = useState<string | null>(null);
  const { data: selectedPlace, isLoading: placeLoading } = usePlace(
    modalPlaceId || undefined
  );

  // Update viewport bounds and zoom when map moves
  const updateMapState = useCallback(() => {
    if (!map) return;

    const leafletBounds = map.getBounds();
    setVisibleBounds({
      north: leafletBounds.getNorth(),
      south: leafletBounds.getSouth(),
      east: leafletBounds.getEast(),
      west: leafletBounds.getWest(),
    });
    setZoom(map.getZoom());
  }, [map]);

  // Handle map ready
  const handleMapReady = useCallback((mapInstance: LeafletMap) => {
    setMap(mapInstance);
  }, []);

  // Set up map event listeners
  useEffect(() => {
    if (!map) return;

    // Initial state
    updateMapState();

    // Listen for map move/zoom events
    map.on('moveend', updateMapState);
    map.on('zoomend', updateMapState);

    return () => {
      map.off('moveend', updateMapState);
      map.off('zoomend', updateMapState);
    };
  }, [map, updateMapState]);

  // Handle place click - show modal
  const handlePlaceClick = useCallback((place: MapPlace) => {
    setSelectedPlace(place.placeId);
    setModalPlaceId(place.id.toString());
  }, [setSelectedPlace]);

  // Handle cluster click - zoom to next tier
  const handleClusterClick = useCallback((cluster: MapCluster) => {
    if (!map) return;
    // Smart zoom based on current tier
    // World (0-7) → Region (10)
    // Region (8-12) → Street (14)
    const targetZoom = tier === 'world' ? 10 : 14;
    map.setView([cluster.lat, cluster.lng], targetZoom);
  }, [map, tier]);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setModalPlaceId(null);
    setSelectedPlace(null);
  }, [setSelectedPlace]);

  // Map bounds for initial fit
  const mapBounds = useMemo(() => {
    if (initialBounds) {
      return boundsToLeaflet(initialBounds);
    }
    return undefined;
  }, [initialBounds]);

  // Count of items being displayed
  const displayCount = clustered ? clusters.length : places.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        margin: -24,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid var(--ant-color-border)',
          background: 'var(--ant-color-bg-container)',
        }}
      >
        <GlobalOutlined
          style={{
            fontSize: 20,
            marginRight: 12,
            color: 'var(--ant-color-primary)',
          }}
        />
        <Title level={4} style={{ margin: 0, flex: 1 }}>
          Maps
        </Title>
        <div style={{ fontSize: 12, color: '#888' }}>
          {clustered ? (
            <>Showing {displayCount} clusters ({totalInBounds.toLocaleString()} places)</>
          ) : (
            <>Showing {displayCount} places (zoom: {zoom})</>
          )}
        </div>
      </div>

      {/* Filter bar with tier-aware controls */}
      <MapFilterBar
        totalPlaces={totalInBounds}
        visiblePlaces={displayCount}
        isLoading={isLoading || isFetching}
        tier={tier}
        curatedCategoriesEnabled={curatedCategoriesEnabled}
        onCuratedCategoriesToggle={handleCuratedCategoriesToggle}
        hasCuratedCategories={hasCuratedCategories}
        visibleCategories={visibleCategoriesList}
        nameBlacklistEnabled={nameBlacklistEnabled}
        onNameBlacklistToggle={isAdmin ? handleNameBlacklistToggle : undefined}
        hasBlacklistPatterns={hasBlacklist}
      />

      {/* Map container */}
      <div style={{ flex: 1, position: 'relative' }}>
        {/* Loading overlay */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
            }}
          >
            <Spin size="large" tip="Loading places..." />
          </div>
        )}

        <MapContainer
          height="100%"
          bounds={mapBounds}
          scrollWheelZoom
          dragging
          zoomControl
          onMapReady={handleMapReady}
        >
          <PlaceMapMarkers
            clusters={clusters}
            places={places}
            clustered={clustered}
            tier={tier}
            selectedPlaceId={selectedPlaceId}
            clusteringEnabled={clusteringEnabled}
            clusterRadius={clusterRadius}
            onPlaceClick={handlePlaceClick}
            onClusterClick={handleClusterClick}
          />
        </MapContainer>
      </div>

      {/* Place detail modal */}
      <PlaceModal
        place={selectedPlace as Place | null}
        open={!!modalPlaceId}
        onClose={handleCloseModal}
        loading={placeLoading}
      />
    </div>
  );
}

export default MapsPage;
